import { useEffect, useState } from "react";
import { Crown, Trophy, Medal, Award, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export function FoundingWall() {
  const [wall, setWall] = useState(null);
  const [podium, setPodium] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get("/founding/wall?limit=100").then((r) => r.data),
      api.get("/founding/podium").then((r) => r.data),
      api.get("/founding/count").then((r) => r.data),
    ])
      .then(([w, p, c]) => {
        setWall(w);
        setPodium(p);
        setCount(c.count || 0);
      })
      .catch(() => setWall([]));
  }, []);

  if (wall === null) return null;
  if (wall.length === 0) return null;

  const podiumIcons = [Trophy, Medal, Award];
  const podiumStyles = [
    "bg-[#C7A34A] text-[#121212]", // Gold
    "bg-[#B8B8B8] text-[#121212]", // Silver
    "bg-[#C08554] text-white",     // Bronze
  ];

  return (
    <section
      data-testid="founding-wall-section"
      className="bg-white py-24 md:py-32 px-6 md:px-12 border-t border-[#E5E5E5]"
    >
      <div className="max-w-6xl mx-auto">
        {/* Podium */}
        {podium.length > 0 && (
          <div className="mb-20 md:mb-24">
            <div className="text-center mb-12">
              <div className="sc-overline mb-4 inline-flex items-center gap-2 justify-center">
                <Sparkles size={13} /> Contest Podio · Founding Ambassador
              </div>
              <h2
                className="font-display font-light text-3xl md:text-4xl mb-4"
                style={{ letterSpacing: "-0.02em" }}
              >
                Il podio dei Founding Member
              </h2>
              <p className="text-neutral-600 text-[15px] max-w-2xl mx-auto">
                Alla chiusura della waitlist, i primi tre Founding Member con più
                inviti si aggiudicano un premio reale: soggiorno gratuito di 4
                giorni, upgrade suite garantito e dinner privato con lo chef del
                progetto.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end max-w-4xl mx-auto">
              {[1, 0, 2].map((sortIndex) => {
                const item = podium[sortIndex];
                if (!item) return <div key={sortIndex} />;
                const Icon = podiumIcons[sortIndex];
                const heights = ["md:pb-8", "md:pb-16", "md:pb-4"];
                const prizes = [
                  "Soggiorno 4gg gratuito · Upgrade Suite · Dinner privato",
                  "Soggiorno 4gg gratuito · Upgrade Suite",
                  "Soggiorno 4gg gratuito",
                ];
                return (
                  <div
                    key={sortIndex}
                    data-testid={`podium-${sortIndex + 1}`}
                    className={`${heights[sortIndex]} flex`}
                  >
                    <div className="bg-white border border-[#E5E5E5] p-8 w-full text-center space-y-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 text-[10px] tracking-[0.2em] uppercase ${podiumStyles[sortIndex]}`}>
                        <Icon size={12} strokeWidth={1.5} /> {sortIndex + 1}° posto
                      </div>
                      <div className="font-mono text-sm text-[#121212]">
                        {item.email_masked}
                      </div>
                      <div>
                        <div className="font-display font-light text-3xl text-[#0B192C]">
                          {item.referrals_count}
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-400 mt-1">
                          inviti
                        </div>
                      </div>
                      <div className="text-[11px] text-neutral-500 leading-relaxed pt-3 border-t border-[#F0F0F0]">
                        {prizes[sortIndex]}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Wall */}
        <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
          <div>
            <div className="sc-overline mb-3 inline-flex items-center gap-2">
              <Crown size={13} /> Founding Wall
            </div>
            <h2
              className="font-display font-light text-3xl md:text-4xl"
              style={{ letterSpacing: "-0.02em" }}
            >
              {count} membri hanno creduto per primi
            </h2>
          </div>
          <div className="text-[11px] text-neutral-500 tracking-[0.2em] uppercase">
            Il tuo nome può essere tra loro
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {wall.map((m) => (
            <div
              key={m.founding_number}
              data-testid={`wall-card-${m.founding_number}`}
              className="border border-[#E5E5E5] p-4 text-center space-y-2 hover:border-[#0B192C] transition-colors"
            >
              <div className="text-[10px] tracking-[0.24em] uppercase text-neutral-400">
                N° {String(m.founding_number).padStart(4, "0")}
              </div>
              <div className="font-mono text-xs text-[#121212] truncate">
                {m.email_masked}
              </div>
              <div className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-[#0B192C]">
                <Crown size={10} /> Platino
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
